import { IApiResponse } from '@interfaces/module.interface';
import {
  IPrintQueue,
  TPosPrintQueueCancelledPayload,
  TPutPrintQueuePayload,
} from '@interfaces/pos-api.interface';
import { api } from '@services/api';
import { handleError } from '@utils/api-error-handling';
import { encryptPayload } from '@utils/security';

// #region API Endpoints
export const printQueueAPI = api
  .enhanceEndpoints({
    addTagTypes: ['Print Queues', 'Print Queues Agent'],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      getPrintQueues: builder.query({
        query: ({
          printQueueID,
        }: {
          printQueueID?: string | null;
          propertyID?: string; // for refetching only
          isOpen?: boolean; // for refetching only
        }) => ({
          url: `integrations/printers/queues${printQueueID ? `/${printQueueID ?? ''}` : ''}`,
        }),
        providesTags: ['Print Queues'],
        async onQueryStarted(_arg, { queryFulfilled }) {
          try {
            await queryFulfilled;
          } catch (err) {
            console.error('Failed to fetch printers:', err);
          }
        },
        transformResponse: (
          response: IApiResponse<{
            results: IPrintQueue[];
          }>,
        ) => response?.data?.results ?? [],
        forceRefetch: () => true,
        transformErrorResponse: (baseQueryReturnValue) =>
          handleError(baseQueryReturnValue),
      }),
      getPrintQueuesAgent: builder.query({
        query: ({}: {
          propertyID?: string; // for refetching only
        }) => ({
          url: 'integrations/printers/queues/agent',
        }),
        providesTags: ['Print Queues Agent'],
        async onQueryStarted(_arg, { queryFulfilled }) {
          try {
            await queryFulfilled;
          } catch (err) {
            console.error('Failed to fetch printers:', err);
          }
        },
        transformResponse: (
          response: IApiResponse<{
            results: {}[];
          }>,
        ) => response?.data,
        forceRefetch: () => true,
        transformErrorResponse: (baseQueryReturnValue) =>
          handleError(baseQueryReturnValue),
      }),
      postPrintQueue: builder.mutation({
        query: (payload: TPutPrintQueuePayload) => ({
          url: 'integrations/printers/queues',
          method: 'POST',
          body: encryptPayload(payload),
        }),
        async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            if (data) {
              dispatch(printQueueAPI.util.invalidateTags(['Print Queues']));
            }
          } catch (error) {}
        },
        transformResponse: async (response: {
          data: [
            {
              responseCode: number;
              responseMessage: string;
            },
          ];
        }) => {
          return response?.data;
        },
        transformErrorResponse: (baseQueryReturnValue) =>
          handleError(baseQueryReturnValue),
      }),
      postPrintQueueCancelled: builder.mutation({
        query: (payload: TPosPrintQueueCancelledPayload) => ({
          url: 'integrations/printers/queues/cancelled',
          method: 'POST',
          body: encryptPayload(payload),
        }),
        async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            if (data) {
              dispatch(printQueueAPI.util.invalidateTags(['Print Queues']));
            }
          } catch (error) {}
        },
        transformResponse: async (response: {
          data: [
            {
              responseCode: number;
              responseMessage: string;
            },
          ];
        }) => {
          return response?.data;
        },
        transformErrorResponse: (baseQueryReturnValue) =>
          handleError(baseQueryReturnValue),
      }),
      putPrintQueue: builder.mutation({
        query: ({
          printQueueID,
          payload,
        }: {
          printQueueID: string;
          payload: TPutPrintQueuePayload;
        }) => ({
          url: `integrations/printers/queues/${printQueueID}`,
          method: 'PUT',
          body: encryptPayload(payload),
        }),
        async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
          const { data } = await queryFulfilled;
          if (data) {
            dispatch(printQueueAPI.util.invalidateTags(['Print Queues']));
          }
        },
        transformResponse: async (response: {
          data: [
            {
              responseCode: number;
              responseMessage: string;
              responsePrintQueueID: string;
            },
          ];
        }) => {
          return response?.data;
        },
        transformErrorResponse: (baseQueryReturnValue) =>
          handleError(baseQueryReturnValue),
      }),
      deletePrintQueue: builder.mutation({
        query: ({ printQueueID }: { printQueueID: string }) => ({
          url: `integrations/printers/queues/${printQueueID}`,
          method: 'DELETE',
        }),
        async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
          const { data } = await queryFulfilled;
          if (data) {
            dispatch(printQueueAPI.util.invalidateTags(['Print Queues']));
          }
        },
        transformResponse: async (response: {
          data: [
            {
              responseCode: number;
              responseMessage: string;
            },
          ];
        }) => {
          return response?.data;
        },
        transformErrorResponse: async (baseQueryReturnValue) => {
          return handleError(baseQueryReturnValue);
        },
      }),
    }),
  });
// #endregion API Endpoints

// #region Export Hooks
export const {
  useGetPrintQueuesQuery,
  useGetPrintQueuesAgentQuery,
  usePostPrintQueueMutation,
  usePostPrintQueueCancelledMutation,
  usePutPrintQueueMutation,
  useDeletePrintQueueMutation,
} = printQueueAPI;
// #endregion Export Hooks
